import type {
  MeetingSummary,
  TranscriptPage,
  TranscriptSearchResult,
  MeetingCore,
  MeetingFacets,
  MeetingParticipant,
  ApiErrorBody,
  AppConfig,
  AuthSession,
  ConnectExtensionResponse,
  MagicLinkResponse,
  MeetingDetail,
  MeetingListItem,
  MeetingListPage,
  Space,
  SpaceDashboard,
  SessionClient,
  SummarizeResponse,
  AsyncSummaryState,
  SummaryTemplate,
  SafetyLimits,
  TokenResponse,
  UserPreferences,
  UserProfile,
  OnboardingProgressResponse,
  Workspace,
  WorkspaceInvitationPreview,
  WorkspaceMembership,
  SearchResponse,
  SearchResult,
  TodayCategory,
  TodayItem,
  TodayResponse,
  EvidenceSpan,
  ReportAction,
  ReportRecord,
  ReportVersionList,
  ReportVersionMetadata,
  ReportLanguage,
  StructuredReport,
  ReportComparison,
  Customer,
  CustomerHistoryResponse,
  CustomerHistoryEvent,
  CustomerMeeting,
  MeetingAssociation,
  CommitmentSuggestion,
  Commitment,
  CommitmentListResponse,
  CommitmentHistoryResponse,
  CommitmentHistoryEvent,
  ExternalTaskLink,
  CustomerMergeConflict,
  CustomerMergeCandidatesResponse,
  CustomerMergePreview,
  CustomerMergeResponse,
  ShareGrant,
  ShareGrantListResponse,
  WorkspaceMemberListResponse,
  WorkspaceMember,
  WorkflowReview,
  WorkflowReviewSource,
  WorkflowPolicyReview,
  WorkflowActionReview,
  WorkflowEffectReview,
  WorkflowReceiptReview,
  WorkflowTransitionReview,
  WorkflowActivityReview,
  WorkflowRunListResponse,
  WorkflowRunListItem,
  WorkflowActivityListResponse,
  WorkflowActivityListItem,
  WorkflowRetryResponse,
  WorkflowRetryItem,
  WorkflowReconciliationResponse,
  WorkflowReconciliationItem,
  WorkspacePolicy,
  WorkflowEvidenceRef,
  WorkflowGoalReview,
  WorkflowGoalStepReview,
  WorkflowActionMutationResult,
  UsageBudget,
  BillingAction,
  BillingStatus,
  BillingSubscription,
  SharedRecap,
  OkResponse,
  ConnectorListResponse,
  Connector,
  ConnectorInitiation,
  ConnectorHealthResponse,
  PrivacyResponse,
  DeletionResult,
  SampleWorkflow,
  ExtensionHealth,
  AudioPeaks,
  AudioPiece,
  AudioPlayback,
  AudioTranscriptionRetry,
  AudioUploadProgress,
  MeetingAudio,
  MeetingAudioSummary,
  TranscriptSource,
} from "./types";
import { isOneOf, MEETING_STATUSES, OVERVIEW_STATUSES, PLATFORMS } from "./constants";

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function isString(value: unknown): value is string {
  return typeof value === "string";
}

function isBoolean(value: unknown): value is boolean {
  return typeof value === "boolean";
}

function isNumber(value: unknown): value is number {
  return typeof value === "number" && Number.isFinite(value);
}

function isStringArray(value: unknown): value is string[] {
  return Array.isArray(value) && value.every(isString);
}

function requireString(value: unknown, message: string): string {
  if (!isString(value) || value.trim().length === 0) throw new Error(message);
  return value;
}

function parseEvidence(value: unknown): EvidenceSpan {
  if (!isRecord(value)) throw new Error("Invalid report evidence");
  if (value.source !== undefined && value.source !== "caption" && value.source !== "chat" && value.source !== "audio_transcription") {
    throw new Error("Unsupported report evidence source");
  }
  if (value.quote !== undefined && !isString(value.quote)) throw new Error("Invalid report evidence quote");
  if (!isNumber(value.revision) || !Number.isInteger(value.revision) || value.revision < 1) throw new Error("Invalid report evidence revision");
  return {
    segment_id: requireString(value.segment_id, "Invalid report evidence"),
    revision: value.revision,
    ...(value.source === undefined ? {} : { source: value.source }),
    ...(value.quote === undefined ? {} : { quote: value.quote }),
  };
}

function parseReportAction(value: unknown): ReportAction {
  if (!isRecord(value) || !Array.isArray(value.evidence)) throw new Error("Invalid structured report item");
  if (value.evidence.length === 0) throw new Error("Structured report item has no evidence");
  if (value.confidence !== undefined && !isString(value.confidence)) throw new Error("Invalid structured report confidence");
  return {
    id: requireString(value.id, "Invalid structured report item"),
    text: requireString(value.text, "Invalid structured report item"),
    evidence: value.evidence.map(parseEvidence),
    ...(value.confidence === undefined ? {} : { confidence: value.confidence }),
  };
}

function parseStructuredReport(value: unknown): StructuredReport {
  if (!isRecord(value)) throw new Error("Invalid structured report");
  if (value.schema_version !== "summary.v1") throw new Error("Unsupported structured report schema");
  if (value.language !== "en" && value.language !== "id" && value.language !== "mixed") throw new Error("Unsupported structured report language");
  const language = value.language as ReportLanguage;
  if (!isString(value.summary) || !Array.isArray(value.action_items) || !Array.isArray(value.decisions) || !Array.isArray(value.open_questions)) throw new Error("Invalid structured report");
  if (value.provenance !== undefined && !Array.isArray(value.provenance)) throw new Error("Invalid structured report provenance");
  return {
    schema_version: "summary.v1" as const,
    language,
    summary: value.summary,
    action_items: value.action_items.map(parseReportAction),
    decisions: value.decisions.map(parseReportAction),
    open_questions: value.open_questions.map(parseReportAction),
    provenance: value.provenance === undefined ? [] : value.provenance.map(parseEvidence),
  };
}

export function parseReportRecord(value: unknown): ReportRecord {
  if (!isRecord(value) || value.schema_version !== "summary.v1") throw new Error("Invalid structured report response");
  if (value.status !== "draft" && value.status !== "published" && value.status !== "stale") throw new Error("Unsupported structured report status");
  if (!isNumber(value.version) || !Number.isInteger(value.version) || value.version < 1) throw new Error("Invalid structured report version");
  const report = parseStructuredReport(value.report);
  if (value.language !== report.language) throw new Error("Structured report language mismatch");
  return {
    report_id: requireString(value.report_id, "Invalid structured report response"),
    meeting_id: requireString(value.meeting_id, "Invalid structured report response"),
    transcript_version_id: requireString(value.transcript_version_id, "Invalid structured report response"),
    schema_version: "summary.v1",
    language: value.language as ReportLanguage,
    status: value.status,
    version: value.version,
    report,
    created_at: requireString(value.created_at, "Invalid structured report response"),
    updated_at: requireString(value.updated_at, "Invalid structured report response"),
  };
}

function parseReportVersionMetadata(value: unknown): ReportVersionMetadata {
  if (!isRecord(value) || value.schema_version !== "summary.v1" || (value.language !== "en" && value.language !== "id" && value.language !== "mixed") || (value.status !== "draft" && value.status !== "published" && value.status !== "stale") || !isNumber(value.version) || !Number.isInteger(value.version) || value.version < 1) {
    throw new Error("Invalid report version metadata");
  }
  return {
    report_id: requireString(value.report_id, "Invalid report version metadata"),
    meeting_id: requireString(value.meeting_id, "Invalid report version metadata"),
    transcript_version_id: requireString(value.transcript_version_id, "Invalid report version metadata"),
    schema_version: "summary.v1",
    language: value.language,
    status: value.status,
    version: value.version,
    created_at: requireString(value.created_at, "Invalid report version metadata"),
    updated_at: requireString(value.updated_at, "Invalid report version metadata"),
  };
}

export function parseReportVersionList(value: unknown): ReportVersionList {
  if (!isRecord(value) || value.contract_version !== "v1" || !Array.isArray(value.versions)) throw new Error("Invalid report version list response");
  return { contract_version: "v1", meeting_id: requireString(value.meeting_id, "Invalid report version list response"), versions: value.versions.map(parseReportVersionMetadata) };
}

export function parseReportComparison(value: unknown): ReportComparison {
  if (!isRecord(value) || value.schema_version !== "summary.v1" || !Array.isArray(value.changes)) {
    throw new Error("Invalid structured report comparison response");
  }
  return {
    schema_version: "summary.v1",
    meeting_id: requireString(value.meeting_id, "Invalid structured report comparison response"),
    from: parseReportRecord(value.from),
    to: parseReportRecord(value.to),
    changes: value.changes.map((change) => {
      if (!isRecord(change)) throw new Error("Invalid structured report comparison change");
      return {
        path: requireString(change.path, "Invalid structured report comparison change"),
        ...(change.before === undefined ? {} : { before: change.before }),
        ...(change.after === undefined ? {} : { after: change.after }),
      };
    }),
  };
}

export function parseCustomer(value: unknown): Customer {
  if (!isRecord(value)) throw new Error("Invalid customer response");
  return {
    customer_id: requireString(value.customer_id, "Invalid customer response"),
    workspace_id: requireString(value.workspace_id, "Invalid customer response"),
    name: requireString(value.name, "Invalid customer response"),
    ...(value.domain === undefined ? {} : { domain: requireString(value.domain, "Invalid customer response") }),
    created_by: requireString(value.created_by, "Invalid customer response"),
    ...(value.version === undefined ? {} : { version: integer(value.version, "Invalid customer response") }),
    ...(value.status === undefined ? {} : { status: enumValue(value.status, ["active", "merged"] as const, "Invalid customer response") }),
    ...(value.merged_into_customer_id === undefined || value.merged_into_customer_id === null ? {} : { merged_into_customer_id: requireString(value.merged_into_customer_id, "Invalid customer response") }),
    ...(value.merged_at === undefined || value.merged_at === null ? {} : { merged_at: requireString(value.merged_at, "Invalid customer response") }),
    created_at: requireString(value.created_at, "Invalid customer response"),
    updated_at: requireString(value.updated_at, "Invalid customer response"),
  };
}

export function parseSpace(value: unknown): Space {
  if (!isRecord(value)) throw new Error("Invalid space response");
  return {
    space_id: requireString(value.space_id, "Invalid space response"),
    workspace_id: requireString(value.workspace_id, "Invalid space response"),
    name: requireString(value.name, "Invalid space response"),
    ...(value.description === undefined || value.description === null ? {} : { description: requireString(value.description, "Invalid space response") }),
    status: enumValue(value.status, ["active", "archived"] as const, "Invalid space response"),
    version: integer(value.version, "Invalid space response"),
    created_by: requireString(value.created_by, "Invalid space response"),
    created_at: requireString(value.created_at, "Invalid space response"),
    updated_at: requireString(value.updated_at, "Invalid space response"),
  };
}

export function parseSpaceList(value: unknown): Space[] {
  if (!isRecord(value) || !Array.isArray(value.items)) throw new Error("Invalid spaces response");
  return value.items.map(parseSpace);
}

export function parseSpaceDashboard(value: unknown): SpaceDashboard {
  if (!isRecord(value) || !Array.isArray(value.open_commitments) || !Array.isArray(value.recent_meetings) || !Array.isArray(value.activity)) throw new Error("Invalid space dashboard response");
  return {
    space: parseSpace(value.space),
    open_commitments: value.open_commitments.map(parseCommitment),
    recent_meetings: parseMeetingList({ meetings: value.recent_meetings }),
    activity: value.activity.map((event) => {
      if (!isRecord(event)) throw new Error("Invalid space activity response");
      return { event_id: requireString(event.event_id, "Invalid space activity response"), event_type: enumValue(event.event_type, ["created", "edited", "archived", "restored", "linked", "unlinked"] as const, "Invalid space activity response"), actor_user_id: requireString(event.actor_user_id, "Invalid space activity response"), created_at: requireString(event.created_at, "Invalid space activity response"), ...(isString(event.meeting_id) ? { meeting_id: event.meeting_id } : {}), ...(isString(event.commitment_id) ? { commitment_id: event.commitment_id } : {}) };
    }),
  };
}

export function parseCustomerList(value: unknown): Customer[] {
  if (!isRecord(value) || !Array.isArray(value.items)) throw new Error("Invalid customers response");
  return value.items.map(parseCustomer);
}

export function parseCustomerHistory(value: unknown): CustomerHistoryResponse {
  if (!isRecord(value) || value.contract_version !== "v1" || !Array.isArray(value.items)) throw new Error("Invalid customer history response");
  return {
    contract_version: "v1",
    customer_id: requireString(value.customer_id, "Invalid customer history response"),
    items: value.items.map((item): CustomerHistoryEvent => {
      if (!isRecord(item) || item.event_type !== "edited" && item.event_type !== "associated" && item.event_type !== "disassociated" && item.event_type !== "merged") throw new Error("Invalid customer history event");
      return {
        event_id: requireString(item.event_id, "Invalid customer history event"),
        customer_id: requireString(item.customer_id, "Invalid customer history event"),
        ...(item.meeting_id === undefined ? {} : { meeting_id: requireString(item.meeting_id, "Invalid customer history event") }),
        event_type: item.event_type,
        actor_user_id: requireString(item.actor_user_id, "Invalid customer history event"),
        ...(item.from_customer_id === undefined || item.from_customer_id === null ? {} : { from_customer_id: requireString(item.from_customer_id, "Invalid customer history event") }),
        ...(item.to_customer_id === undefined || item.to_customer_id === null ? {} : { to_customer_id: requireString(item.to_customer_id, "Invalid customer history event") }),
        ...(item.before === undefined || item.before === null ? {} : { before: isRecord(item.before) ? item.before : (() => { throw new Error("Invalid customer history event"); })() }),
        ...(item.after === undefined || item.after === null ? {} : { after: isRecord(item.after) ? item.after : (() => { throw new Error("Invalid customer history event"); })() }),
        created_at: requireString(item.created_at, "Invalid customer history event"),
      };
    }),
  };
}

export function parseCustomerMeetingList(value: unknown): CustomerMeeting[] {
  if (!isRecord(value) || !Array.isArray(value.items)) throw new Error("Invalid customer meetings response");
  return value.items.map((item) => {
    if (!isRecord(item)) throw new Error("Invalid customer meeting response");
    return {
      meeting_id: requireString(item.meeting_id, "Invalid customer meeting response"),
      title: requireString(item.title, "Invalid customer meeting response"),
      status: requireString(item.status, "Invalid customer meeting response"),
      started_at: requireString(item.started_at, "Invalid customer meeting response"),
      association_type: requireString(item.association_type, "Invalid customer meeting response"),
    };
  });
}

export function parseMeetingAssociation(value: unknown): MeetingAssociation {
  if (!isRecord(value)) throw new Error("Invalid meeting association response");
  if (value.association_type !== "suggested" && value.association_type !== "confirmed") throw new Error("Unsupported meeting association type");
  if (value.confidence !== undefined && (!isNumber(value.confidence) || value.confidence < 0 || value.confidence > 1)) throw new Error("Invalid meeting association confidence");
  return {
    meeting_id: requireString(value.meeting_id, "Invalid meeting association response"),
    customer_id: requireString(value.customer_id, "Invalid meeting association response"),
    association_type: value.association_type,
    ...(value.confidence === undefined ? {} : { confidence: value.confidence }),
    ...(value.confirmed_at === undefined ? {} : { confirmed_at: requireString(value.confirmed_at, "Invalid meeting association response") }),
  };
}

export function parseCommitmentSuggestion(value: unknown): CommitmentSuggestion {
  if (!isRecord(value) || !Array.isArray(value.evidence)) throw new Error("Invalid commitment suggestion response");
  if (value.status !== "suggested" && value.status !== "confirmed" && value.status !== "rejected") throw new Error("Unsupported commitment suggestion status");
  const evidence = value.evidence.map((item) => {
    if (!isRecord(item)) throw new Error("Invalid commitment suggestion evidence");
    return item;
  });
  return {
    suggestion_id: requireString(value.suggestion_id, "Invalid commitment suggestion response"),
    meeting_id: requireString(value.meeting_id, "Invalid commitment suggestion response"),
    ...(value.customer_id === undefined ? {} : { customer_id: requireString(value.customer_id, "Invalid commitment suggestion response") }),
    ...(value.source_report_id === undefined ? {} : { source_report_id: requireString(value.source_report_id, "Invalid commitment suggestion response") }),
    ...(value.source_action_id === undefined ? {} : { source_action_id: requireString(value.source_action_id, "Invalid commitment suggestion response") }),
    text: requireString(value.text, "Invalid commitment suggestion response"),
    ...(value.due_phrase === undefined ? {} : { due_phrase: requireString(value.due_phrase, "Invalid commitment suggestion response") }),
    evidence,
    status: value.status,
    created_at: requireString(value.created_at, "Invalid commitment suggestion response"),
  };
}

export function parseCommitment(value: unknown): Commitment {
  if (!isRecord(value)) throw new Error("Invalid commitment response");
  if (value.status !== "open" && value.status !== "completed" && value.status !== "cancelled") throw new Error("Unsupported commitment status");
  return {
    commitment_id: requireString(value.commitment_id, "Invalid commitment response"),
    meeting_id: requireString(value.meeting_id, "Invalid commitment response"),
    ...(value.customer_id === undefined ? {} : { customer_id: requireString(value.customer_id, "Invalid commitment response") }),
    suggestion_id: requireString(value.suggestion_id, "Invalid commitment response"),
    owner_user_id: requireString(value.owner_user_id, "Invalid commitment response"),
    text: requireString(value.text, "Invalid commitment response"),
    ...(value.due_phrase === undefined ? {} : { due_phrase: requireString(value.due_phrase, "Invalid commitment response") }),
    ...(value.due_at === undefined || value.due_at === null ? {} : { due_at: requireString(value.due_at, "Invalid commitment response") }),
    timezone: requireString(value.timezone, "Invalid commitment response"),
    status: value.status,
    ...(value.version === undefined ? {} : { version: integer(value.version, "Invalid commitment response") }),
    ...(value.external_task_links === undefined ? {} : { external_task_links: parseExternalTaskLinks(value).items }),
    created_at: requireString(value.created_at, "Invalid commitment response"),
    updated_at: requireString(value.updated_at, "Invalid commitment response"),
    ...(Array.isArray(value.spaces) ? { spaces: value.spaces.map(parseSpace) } : {}),
  };
}

export function parseExternalTaskLink(value: unknown): ExternalTaskLink {
  if (!isRecord(value)) throw new Error("Invalid external task link response");
  return {
    link_id: requireString(value.link_id, "Invalid external task link response"),
    provider: requireString(value.provider, "Invalid external task link response"),
    external_task_id: requireString(value.external_task_id, "Invalid external task link response"),
    ...(value.url === undefined || value.url === null ? {} : { url: requireString(value.url, "Invalid external task link response") }),
    created_by: requireString(value.created_by, "Invalid external task link response"),
    created_at: requireString(value.created_at, "Invalid external task link response"),
  };
}

export function parseExternalTaskLinksResponse(value: unknown): { commitment_id: string; items: ExternalTaskLink[] } {
  if (!isRecord(value) || value.contract_version !== "commitment-external-task-links.v1" || !Array.isArray(value.items)) throw new Error("Invalid external task links response");
  return { commitment_id: requireString(value.commitment_id, "Invalid external task links response"), items: value.items.map(parseExternalTaskLink) };
}

function parseExternalTaskLinks(value: unknown): { items: ExternalTaskLink[] } {
  if (!isRecord(value) || !Array.isArray(value.external_task_links)) throw new Error("Invalid external task link response");
  return { items: value.external_task_links.map(parseExternalTaskLink) };
}

function parseMergeCustomer(value: unknown): Customer {
  const customer = parseCustomer(value);
  if (customer.version === undefined || customer.status === undefined) throw new Error("Invalid customer merge response");
  return customer;
}

function parseMergeConflict(value: unknown): CustomerMergeConflict {
  if (!isRecord(value)) throw new Error("Invalid customer merge conflict");
  return {
    type: enumValue(value.type, ["external_id_conflict", "meeting_association_conflict"] as const, "Invalid customer merge conflict"),
    ...(value.provider === undefined ? {} : { provider: requireString(value.provider, "Invalid customer merge conflict") }),
    ...(value.external_id === undefined ? {} : { external_id: requireString(value.external_id, "Invalid customer merge conflict") }),
    ...(value.meeting_id === undefined ? {} : { meeting_id: requireString(value.meeting_id, "Invalid customer merge conflict") }),
    ...(value.source_record_id === undefined ? {} : { source_record_id: requireString(value.source_record_id, "Invalid customer merge conflict") }),
    ...(value.target_record_id === undefined ? {} : { target_record_id: requireString(value.target_record_id, "Invalid customer merge conflict") }),
    ...(value.detail === undefined ? {} : { detail: requireString(value.detail, "Invalid customer merge conflict") }),
  };
}

export function parseCustomerMergeCandidates(value: unknown): CustomerMergeCandidatesResponse {
  if (!isRecord(value) || value.contract_version !== "customer-merge-candidates.v1" || !Array.isArray(value.items)) throw new Error("Invalid customer merge candidates response");
  return { contract_version: "customer-merge-candidates.v1", source_customer: parseMergeCustomer(value.source_customer), items: value.items.map((item) => { if (!isRecord(item) || !Array.isArray(item.conflicts)) throw new Error("Invalid customer merge candidate"); return { customer: parseMergeCustomer(item.customer), conflicts: item.conflicts.map(parseMergeConflict) }; }) };
}

export function parseCustomerMergePreview(value: unknown): CustomerMergePreview {
  if (!isRecord(value) || value.contract_version !== "customer-merge-preview.v1" || !Array.isArray(value.conflicts) || typeof value.can_confirm !== "boolean") throw new Error("Invalid customer merge preview response");
  return { contract_version: "customer-merge-preview.v1", source_customer: parseMergeCustomer(value.source_customer), target_customer: parseMergeCustomer(value.target_customer), conflicts: value.conflicts.map(parseMergeConflict), can_confirm: value.can_confirm };
}

export function parseCustomerMergeResponse(value: unknown): CustomerMergeResponse {
  if (!isRecord(value) || value.contract_version !== "customer-merge.v1") throw new Error("Invalid customer merge response");
  return { contract_version: "customer-merge.v1", customer: parseMergeCustomer(value.customer), source_customer_id: requireString(value.source_customer_id, "Invalid customer merge response"), merged_customer_id: requireString(value.merged_customer_id, "Invalid customer merge response") };
}

export function parseCommitmentList(value: unknown): CommitmentListResponse {
  if (!isRecord(value) || value.contract_version !== "v1" || !Array.isArray(value.items)) throw new Error("Invalid commitment list response");
  return { contract_version: "v1", items: value.items.map(parseCommitment) };
}

export function parseCommitmentHistory(value: unknown): CommitmentHistoryResponse {
  if (!isRecord(value) || value.contract_version !== "v1" || !Array.isArray(value.items)) throw new Error("Invalid commitment history response");
  return {
    contract_version: "v1",
    commitment_id: requireString(value.commitment_id, "Invalid commitment history response"),
    items: value.items.map((item): CommitmentHistoryEvent => {
      if (!isRecord(item) || item.to_status !== "open" && item.to_status !== "completed" && item.to_status !== "cancelled") throw new Error("Invalid commitment history event");
      if (item.from_status !== undefined && item.from_status !== null && item.from_status !== "open" && item.from_status !== "completed" && item.from_status !== "cancelled") throw new Error("Invalid commitment history event");
      return {
        event_id: requireString(item.event_id, "Invalid commitment history event"),
        commitment_id: requireString(item.commitment_id, "Invalid commitment history event"),
        actor_user_id: requireString(item.actor_user_id, "Invalid commitment history event"),
        ...(item.from_status === undefined || item.from_status === null ? {} : { from_status: item.from_status }),
        to_status: item.to_status,
        ...(item.event_type === undefined ? {} : { event_type: requireString(item.event_type, "Invalid commitment history event") }),
        ...(item.before === undefined || item.before === null ? {} : { before: isRecord(item.before) ? item.before : (() => { throw new Error("Invalid commitment history event"); })() }),
        ...(item.after === undefined || item.after === null ? {} : { after: isRecord(item.after) ? item.after : (() => { throw new Error("Invalid commitment history event"); })() }),
        created_at: requireString(item.created_at, "Invalid commitment history event"),
      };
    }),
  };
}

export function parseShareGrant(value: unknown): ShareGrant {
  if (!isRecord(value)) throw new Error("Invalid share response");
  if (value.capability !== "recap" && value.capability !== "full_transcript" && value.capability !== "audio") throw new Error("Unsupported share capability");
  return {
    share_id: requireString(value.share_id, "Invalid share response"),
    meeting_id: requireString(value.meeting_id, "Invalid share response"),
    recipient_user_id: requireString(value.recipient_user_id, "Invalid share response"),
    capability: value.capability,
    ...(value.expires_at === undefined || value.expires_at === null ? {} : { expires_at: requireString(value.expires_at, "Invalid share response") }),
    ...(value.revoked_at === undefined || value.revoked_at === null ? {} : { revoked_at: requireString(value.revoked_at, "Invalid share response") }),
    created_at: requireString(value.created_at, "Invalid share response"),
  };
}

export function parseShareGrantList(value: unknown): ShareGrantListResponse {
  if (!isRecord(value) || value.contract_version !== "v1" || !Array.isArray(value.items)) throw new Error("Invalid share list response");
  return { contract_version: "v1", meeting_id: requireString(value.meeting_id, "Invalid share list response"), items: value.items.map(parseShareGrant) };
}

export function parseWorkspaceMemberList(value: unknown): WorkspaceMemberListResponse {
  if (!isRecord(value) || value.contract_version !== "v1" || !Array.isArray(value.items)) throw new Error("Invalid workspace member response");
  return {
    contract_version: "v1",
    workspace_id: requireString(value.workspace_id, "Invalid workspace member response"),
    items: value.items.map((item): WorkspaceMember => {
      if (!isRecord(item) || item.status !== "active" || item.role !== "owner" && item.role !== "admin" && item.role !== "member" && item.role !== "billing_admin") throw new Error("Invalid workspace member response");
      return { user_id: requireString(item.user_id, "Invalid workspace member response"), display_name: requireString(item.display_name, "Invalid workspace member response"), role: item.role, status: "active", joined_at: requireString(item.joined_at, "Invalid workspace member response") };
    }),
  };
}

const workflowStates = ["preparing", "needs_information", "needs_review", "executing", "partially_completed", "completed", "paused", "cancelled", "expired", "blocked", "reconciling"] as const;
const workflowKinds = ["clarification", "crm_note", "crm_task", "email_draft", "email_send"] as const;
const workflowActionStatuses = ["pending", "blocked", "approved", "verified", "failed", "reconciling", "manual"] as const;
const workflowEffectStatuses = ["queued", "running", "reconciling", "succeeded", "failed", "cancelled"] as const;
const workflowReceiptStatuses = ["verified", "reconciling", "failed", "blocked"] as const;
function enumValue<T extends readonly string[]>(value: unknown, values: T, message: string): T[number] { if (typeof value !== "string" || !values.includes(value)) throw new Error(message); return value as T[number]; }
function integer(value: unknown, message: string): number { if (typeof value !== "number" || !Number.isInteger(value) || value < 1) throw new Error(message); return value; }
function parseWorkflowSource(value: unknown): WorkflowReviewSource { if (!isRecord(value)) throw new Error("Invalid workflow source"); return { version: requireString(value.version, "Invalid workflow source"), digest: requireString(value.digest, "Invalid workflow source") }; }

export function parseWorkflowReview(value: unknown): WorkflowReview {
  if (!isRecord(value) || value.schema_version !== "workflow-review.v1" || !Array.isArray(value.actions) || !Array.isArray(value.effects) || !Array.isArray(value.receipts) || !Array.isArray(value.transitions) || !Array.isArray(value.activity) || !isRecord(value.policy)) throw new Error("Invalid workflow review response");
  const policy = value.policy;
  if (!isBoolean(policy.approval_required) || !isBoolean(policy.email_send_requires_review) || !isNumber(policy.max_external_mutations) || !Number.isInteger(policy.max_external_mutations) || policy.max_external_mutations < 1 || policy.max_external_mutations > 10 || !isNumber(policy.max_retryable_attempts) || !Number.isInteger(policy.max_retryable_attempts) || policy.max_retryable_attempts < 1 || policy.max_retryable_attempts > 3 || !isNumber(policy.approval_expiry_hours) || !Number.isInteger(policy.approval_expiry_hours) || policy.approval_expiry_hours < 1 || policy.approval_expiry_hours > 24) throw new Error("Invalid workflow policy review");
  const actions = value.actions.map((item): WorkflowActionReview => {
    if (!isRecord(item) || !isRecord(item.payload)) throw new Error("Invalid workflow action review");
    return { action_id: requireString(item.action_id, "Invalid workflow action review"), step_id: requireString(item.step_id, "Invalid workflow action review"), version: integer(item.version, "Invalid workflow action review"), kind: enumValue(item.kind, workflowKinds, "Invalid workflow action review"), status: enumValue(item.status, workflowActionStatuses, "Invalid workflow action review"), payload: item.payload, payload_hash: requireString(item.payload_hash, "Invalid workflow action review"), source: parseWorkflowSource(item.source), requires_approval: isBoolean(item.requires_approval) ? item.requires_approval : (() => { throw new Error("Invalid workflow action review"); })(), created_at: requireString(item.created_at, "Invalid workflow action review") };
  });
  const effects = value.effects.map((item): WorkflowEffectReview => { if (!isRecord(item)) throw new Error("Invalid workflow effect review"); return { effect_id: requireString(item.effect_id, "Invalid workflow effect review"), action_id: requireString(item.action_id, "Invalid workflow effect review"), status: enumValue(item.status, workflowEffectStatuses, "Invalid workflow effect review"), attempt: integer(item.attempt, "Invalid workflow effect review"), max_attempts: integer(item.max_attempts, "Invalid workflow effect review"), created_at: requireString(item.created_at, "Invalid workflow effect review"), updated_at: requireString(item.updated_at, "Invalid workflow effect review"), ...(item.completed_at === undefined || item.completed_at === null ? {} : { completed_at: requireString(item.completed_at, "Invalid workflow effect review") }) }; });
  const receipts = value.receipts.map((item): WorkflowReceiptReview => { if (!isRecord(item)) throw new Error("Invalid workflow receipt review"); return { receipt_id: requireString(item.receipt_id, "Invalid workflow receipt review"), effect_id: requireString(item.effect_id, "Invalid workflow receipt review"), action_id: requireString(item.action_id, "Invalid workflow receipt review"), step_id: requireString(item.step_id, "Invalid workflow receipt review"), status: enumValue(item.status, workflowReceiptStatuses, "Invalid workflow receipt review"), ...(item.provider_object_id === undefined ? {} : { provider_object_id: requireString(item.provider_object_id, "Invalid workflow receipt review") }), ...(item.response_hash === undefined ? {} : { response_hash: requireString(item.response_hash, "Invalid workflow receipt review") }), created_at: requireString(item.created_at, "Invalid workflow receipt review") }; });
  const transitions = value.transitions.map((item): WorkflowTransitionReview => { if (!isRecord(item)) throw new Error("Invalid workflow transition review"); return { transition_id: requireString(item.transition_id, "Invalid workflow transition review"), from_state: enumValue(item.from_state, workflowStates, "Invalid workflow transition review"), to_state: enumValue(item.to_state, workflowStates, "Invalid workflow transition review"), expected_state_version: integer(item.expected_state_version, "Invalid workflow transition review"), source: parseWorkflowSource(item.source), ...(item.preparation_digest === undefined ? {} : { preparation_digest: requireString(item.preparation_digest, "Invalid workflow transition review") }), idempotency_key: requireString(item.idempotency_key, "Invalid workflow transition review"), created_at: requireString(item.created_at, "Invalid workflow transition review") }; });
  const activity = value.activity.map((item): WorkflowActivityReview => { if (!isRecord(item)) throw new Error("Invalid workflow activity review"); const activityType = item.activity_type === "transition" || item.activity_type === "effect_attempt" ? item.activity_type : (() => { throw new Error("Invalid workflow activity review"); })(); return { activity_id: requireString(item.activity_id, "Invalid workflow activity review"), activity_type: activityType, occurred_at: requireString(item.occurred_at, "Invalid workflow activity review"), ...(item.transition_id === undefined ? {} : { transition_id: requireString(item.transition_id, "Invalid workflow activity review") }), ...(item.effect_attempt_id === undefined ? {} : { effect_attempt_id: requireString(item.effect_attempt_id, "Invalid workflow activity review") }), ...(item.effect_id === undefined ? {} : { effect_id: requireString(item.effect_id, "Invalid workflow activity review") }), ...(item.action_id === undefined ? {} : { action_id: requireString(item.action_id, "Invalid workflow activity review") }), ...(item.attempt === undefined ? {} : { attempt: integer(item.attempt, "Invalid workflow activity review") }), ...(item.status === undefined ? {} : { status: requireString(item.status, "Invalid workflow activity review") }), ...(item.error_category === undefined ? {} : { error_category: requireString(item.error_category, "Invalid workflow activity review") }), ...(item.from_state === undefined ? {} : { from_state: enumValue(item.from_state, workflowStates, "Invalid workflow activity review") }), ...(item.to_state === undefined ? {} : { to_state: enumValue(item.to_state, workflowStates, "Invalid workflow activity review") }), ...(item.expected_state_version === undefined ? {} : { expected_state_version: integer(item.expected_state_version, "Invalid workflow activity review") }) }; });
  const policyReview: WorkflowPolicyReview = { approval_required: policy.approval_required, email_send_requires_review: policy.email_send_requires_review, max_external_mutations: policy.max_external_mutations, max_retryable_attempts: policy.max_retryable_attempts, approval_expiry_hours: policy.approval_expiry_hours };
  return { schema_version: "workflow-review.v1", job_id: requireString(value.job_id, "Invalid workflow review response"), workspace_id: requireString(value.workspace_id, "Invalid workflow review response"), goal_id: requireString(value.goal_id, "Invalid workflow review response"), state: enumValue(value.state, workflowStates, "Invalid workflow review response"), state_version: integer(value.state_version, "Invalid workflow review response"), policy_version: integer(value.policy_version, "Invalid workflow review response"), policy: policyReview, source: parseWorkflowSource(value.source), created_at: requireString(value.created_at, "Invalid workflow review response"), updated_at: requireString(value.updated_at, "Invalid workflow review response"), ...(value.completed_at === undefined || value.completed_at === null ? {} : { completed_at: requireString(value.completed_at, "Invalid workflow review response") }), actions, effects, receipts, transitions, activity };
}

export function parseWorkflowRunList(value: unknown): WorkflowRunListResponse {
  if (!isRecord(value) || value.schema_version !== "workflow-runs.v1" || !Array.isArray(value.items) || (value.next_cursor !== null && value.next_cursor !== undefined && !isString(value.next_cursor))) throw new Error("Invalid workflow run list response");
  return { schema_version: "workflow-runs.v1", workspace_id: requireString(value.workspace_id, "Invalid workflow run list response"), items: value.items.map((item): WorkflowRunListItem => { if (!isRecord(item) || !isRecord(item.receipt_counts)) throw new Error("Invalid workflow run list item"); return { job_id: requireString(item.job_id, "Invalid workflow run list item"), goal_id: requireString(item.goal_id, "Invalid workflow run list item"), state: enumValue(item.state, workflowStates, "Invalid workflow run list item"), state_version: integer(item.state_version, "Invalid workflow run list item"), source: parseWorkflowSource(item.source), created_at: requireString(item.created_at, "Invalid workflow run list item"), updated_at: requireString(item.updated_at, "Invalid workflow run list item"), ...(item.completed_at === undefined || item.completed_at === null ? {} : { completed_at: requireString(item.completed_at, "Invalid workflow run list item") }), retry_remaining: nonNegativeInteger(item.retry_remaining, "Invalid workflow run list item"), receipt_counts: { verified: nonNegativeInteger(item.receipt_counts.verified, "Invalid workflow run list item"), reconciling: nonNegativeInteger(item.receipt_counts.reconciling, "Invalid workflow run list item"), failed: nonNegativeInteger(item.receipt_counts.failed, "Invalid workflow run list item"), blocked: nonNegativeInteger(item.receipt_counts.blocked, "Invalid workflow run list item") }, has_reconciliation: isBoolean(item.has_reconciliation) ? item.has_reconciliation : (() => { throw new Error("Invalid workflow run list item"); })() }; }), next_cursor: value.next_cursor === undefined ? null : value.next_cursor };
}

function nonNegativeInteger(value: unknown, message: string): number { if (typeof value !== "number" || !Number.isInteger(value) || value < 0) throw new Error(message); return value; }

export function parseWorkflowActivityList(value: unknown): WorkflowActivityListResponse {
  if (!isRecord(value) || value.schema_version !== "workflow-activity.v1" || !Array.isArray(value.items) || (value.next_cursor !== null && value.next_cursor !== undefined && !isString(value.next_cursor))) throw new Error("Invalid workflow activity response");
  return { schema_version: "workflow-activity.v1", workspace_id: requireString(value.workspace_id, "Invalid workflow activity response"), items: value.items.map((item): WorkflowActivityListItem => { if (!isRecord(item)) throw new Error("Invalid workflow activity item"); return { activity_id: requireString(item.activity_id, "Invalid workflow activity item"), job_id: requireString(item.job_id, "Invalid workflow activity item"), activity_type: enumValue(item.activity_type, ["transition", "effect_attempt"] as const, "Invalid workflow activity item"), occurred_at: requireString(item.occurred_at, "Invalid workflow activity item"), ...(item.effect_id === undefined ? {} : { effect_id: requireString(item.effect_id, "Invalid workflow activity item") }), ...(item.action_id === undefined ? {} : { action_id: requireString(item.action_id, "Invalid workflow activity item") }), ...(item.attempt === undefined ? {} : { attempt: nonNegativeInteger(item.attempt, "Invalid workflow activity item") }), ...(item.status === undefined ? {} : { status: requireString(item.status, "Invalid workflow activity item") }), ...(item.error_category === undefined ? {} : { error_category: requireString(item.error_category, "Invalid workflow activity item") }), ...(item.error_code === undefined ? {} : { error_code: requireString(item.error_code, "Invalid workflow activity item") }), ...(item.error_detail === undefined ? {} : { error_detail: requireString(item.error_detail, "Invalid workflow activity item") }), ...(item.from_state === undefined ? {} : { from_state: enumValue(item.from_state, workflowStates, "Invalid workflow activity item") }), ...(item.to_state === undefined ? {} : { to_state: enumValue(item.to_state, workflowStates, "Invalid workflow activity item") }), ...(item.expected_state_version === undefined ? {} : { expected_state_version: integer(item.expected_state_version, "Invalid workflow activity item") }) }; }), next_cursor: value.next_cursor === undefined ? null : value.next_cursor };
}

function parseWorkflowRetryItem(value: unknown): WorkflowRetryItem { if (!isRecord(value)) throw new Error("Invalid workflow retry item"); return { action_id: requireString(value.action_id, "Invalid workflow retry item"), effect_id: requireString(value.effect_id, "Invalid workflow retry item"), attempt: nonNegativeInteger(value.attempt, "Invalid workflow retry item"), max_attempts: integer(value.max_attempts, "Invalid workflow retry item"), remaining_attempts: nonNegativeInteger(value.remaining_attempts, "Invalid workflow retry item"), reason: requireString(value.reason, "Invalid workflow retry item") }; }
export function parseWorkflowRetry(value: unknown): WorkflowRetryResponse { if (!isRecord(value) || value.schema_version !== "workflow-retry.v1" || !Array.isArray(value.queued) || !Array.isArray(value.reconciliation_required)) throw new Error("Invalid workflow retry response"); return { schema_version: "workflow-retry.v1", workspace_id: requireString(value.workspace_id, "Invalid workflow retry response"), job_id: requireString(value.job_id, "Invalid workflow retry response"), status: enumValue(value.status, ["queued", "no_retryable_work", "reconciliation_required"] as const, "Invalid workflow retry response"), queued: value.queued.map(parseWorkflowRetryItem), reconciliation_required: value.reconciliation_required.map(parseWorkflowRetryItem), retry_remaining: nonNegativeInteger(value.retry_remaining, "Invalid workflow retry response") }; }

export function parseWorkflowReconciliation(value: unknown): WorkflowReconciliationResponse { if (!isRecord(value) || value.schema_version !== "workflow-reconciliation.v1" || !Array.isArray(value.items)) throw new Error("Invalid workflow reconciliation response"); return { schema_version: "workflow-reconciliation.v1", workspace_id: requireString(value.workspace_id, "Invalid workflow reconciliation response"), job_id: requireString(value.job_id, "Invalid workflow reconciliation response"), status: enumValue(value.status, ["clear", "reconciling"] as const, "Invalid workflow reconciliation response"), items: value.items.map((item): WorkflowReconciliationItem => { if (!isRecord(item) || !isRecord(item.receipt)) throw new Error("Invalid workflow reconciliation item"); return { effect_id: requireString(item.effect_id, "Invalid workflow reconciliation item"), action_id: requireString(item.action_id, "Invalid workflow reconciliation item"), attempt: nonNegativeInteger(item.attempt, "Invalid workflow reconciliation item"), max_attempts: integer(item.max_attempts, "Invalid workflow reconciliation item"), status: enumValue(item.status, ["reconciling", "running"] as const, "Invalid workflow reconciliation item"), receipt: { receipt_id: requireString(item.receipt.receipt_id, "Invalid workflow reconciliation receipt"), status: enumValue(item.receipt.status, workflowReceiptStatuses, "Invalid workflow reconciliation receipt"), ...(item.receipt.provider_object_id === undefined ? {} : { provider_object_id: requireString(item.receipt.provider_object_id, "Invalid workflow reconciliation receipt") }), ...(item.receipt.response_hash === undefined ? {} : { response_hash: requireString(item.receipt.response_hash, "Invalid workflow reconciliation receipt") }), ...(item.receipt.error_category === undefined ? {} : { error_category: requireString(item.receipt.error_category, "Invalid workflow reconciliation receipt") }), ...(item.receipt.error_code === undefined ? {} : { error_code: requireString(item.receipt.error_code, "Invalid workflow reconciliation receipt") }), ...(item.receipt.error_detail === undefined ? {} : { error_detail: requireString(item.receipt.error_detail, "Invalid workflow reconciliation receipt") }), created_at: requireString(item.receipt.created_at, "Invalid workflow reconciliation receipt"), ...(item.receipt.reconciled_at === undefined || item.receipt.reconciled_at === null ? {} : { reconciled_at: requireString(item.receipt.reconciled_at, "Invalid workflow reconciliation receipt") }) } }; }) }; }

export function parseWorkspacePolicy(value: unknown): WorkspacePolicy { if (!isRecord(value) || value.contract_version !== "workspace-policy.v1" || !isRecord(value.preferences) || !isRecord(value.autonomy) || value.autonomy.email_send !== "review_required") throw new Error("Invalid workspace policy response"); return { contract_version: "workspace-policy.v1", workspace_id: requireString(value.workspace_id, "Invalid workspace policy response"), version: integer(value.version, "Invalid workspace policy response"), preferences: { default_visibility: enumValue(value.preferences.default_visibility, ["private", "workspace"] as const, "Invalid workspace policy response") }, autonomy: { crm_notes_and_tasks: isBoolean(value.autonomy.crm_notes_and_tasks) ? value.autonomy.crm_notes_and_tasks : (() => { throw new Error("Invalid workspace policy response"); })(), email_send: "review_required" }, updated_at: requireString(value.updated_at, "Invalid workspace policy response") }; }

function parseEvidenceRef(value: unknown): WorkflowEvidenceRef { if (!isRecord(value)) throw new Error("Invalid workflow evidence reference"); return { ref_type: enumValue(value.ref_type, ["meeting", "transcript_segment", "report", "customer", "commitment"] as const, "Invalid workflow evidence reference"), ref_id: requireString(value.ref_id, "Invalid workflow evidence reference"), ...(value.revision === undefined ? {} : { revision: nonNegativeInteger(value.revision, "Invalid workflow evidence reference") }), ...(value.label === undefined ? {} : { label: requireString(value.label, "Invalid workflow evidence reference") }), ...(value.quote === undefined ? {} : { quote: requireString(value.quote, "Invalid workflow evidence reference") }) }; }
export function parseWorkflowGoalReview(value: unknown): WorkflowGoalReview { if (!isRecord(value) || value.schema_version !== "workflow-goal.v1" || !Array.isArray(value.steps)) throw new Error("Invalid workflow goal review response"); return { schema_version: "workflow-goal.v1", goal_id: requireString(value.goal_id, "Invalid workflow goal review response"), workspace_id: requireString(value.workspace_id, "Invalid workflow goal review response"), job_id: requireString(value.job_id, "Invalid workflow goal review response"), version: integer(value.version, "Invalid workflow goal review response"), title: requireString(value.title, "Invalid workflow goal review response"), objective: requireString(value.objective, "Invalid workflow goal review response"), state: enumValue(value.state, workflowStates, "Invalid workflow goal review response"), state_version: integer(value.state_version, "Invalid workflow goal review response"), policy_version: integer(value.policy_version, "Invalid workflow goal review response"), source: parseWorkflowSource(value.source), evidence_refs: Array.isArray(value.evidence_refs) ? value.evidence_refs.map(parseEvidenceRef) : (() => { throw new Error("Invalid workflow goal review response"); })(), steps: value.steps.map((item): WorkflowGoalStepReview => { if (!isRecord(item) || !isRecord(item.payload) || !Array.isArray(item.evidence_refs)) throw new Error("Invalid workflow goal step review"); return { ...(item.action_id === undefined ? {} : { action_id: requireString(item.action_id, "Invalid workflow goal step review") }), step_id: requireString(item.step_id, "Invalid workflow goal step review"), ...(item.version === undefined ? {} : { version: integer(item.version, "Invalid workflow goal step review") }), kind: enumValue(item.kind, workflowKinds, "Invalid workflow goal step review"), status: requireString(item.status, "Invalid workflow goal step review"), review_state: requireString(item.review_state, "Invalid workflow goal step review"), selected: isBoolean(item.selected) ? item.selected : (() => { throw new Error("Invalid workflow goal step review"); })(), payload: item.payload, ...(item.payload_hash === undefined ? {} : { payload_hash: requireString(item.payload_hash, "Invalid workflow goal step review") }), source: parseWorkflowSource(item.source), reason: typeof item.reason === "string" ? item.reason : "", evidence_refs: item.evidence_refs.map(parseEvidenceRef) }; }) }; }
export function parseWorkflowActionMutationResult(value: unknown): WorkflowActionMutationResult { if (!isRecord(value) || value.schema_version !== "workflow-action-review.v1" || !isRecord(value.payload) || !Array.isArray(value.evidence_refs)) throw new Error("Invalid workflow action mutation response"); return { schema_version: "workflow-action-review.v1", action_id: requireString(value.action_id, "Invalid workflow action mutation response"), step_id: requireString(value.step_id, "Invalid workflow action mutation response"), version: integer(value.version, "Invalid workflow action mutation response"), status: requireString(value.status, "Invalid workflow action mutation response"), review_state: requireString(value.review_state, "Invalid workflow action mutation response"), selected: isBoolean(value.selected) ? value.selected : (() => { throw new Error("Invalid workflow action mutation response"); })(), kind: enumValue(value.kind, workflowKinds, "Invalid workflow action mutation response"), payload: value.payload, payload_hash: requireString(value.payload_hash, "Invalid workflow action mutation response"), source: parseWorkflowSource(value.source), reason: typeof value.reason === "string" ? value.reason : "", evidence_refs: value.evidence_refs.map(parseEvidenceRef) }; }

const billingModes = ["bypass", "shadow", "enforced"] as const;
const billingStates = ["no_subscription", "trialing", "active", "grace", "payment_failed", "cancellation_scheduled", "paused", "unpaid", "cancelled", "expired", "incomplete", "incomplete_expired"] as const;
function optionalDate(value: unknown, message: string): string | undefined { return value === undefined || value === null ? undefined : requireString(value, message); }
function parseBillingSubscription(value: unknown): BillingSubscription {
  if (!isRecord(value) || typeof value.cancel_at_period_end !== "boolean" || !isNumber(value.payment_failure_count) || !Number.isInteger(value.payment_failure_count) || value.payment_failure_count < 0 || !isNumber(value.amount_minor) || !Number.isInteger(value.amount_minor) || !isNumber(value.seat_count) || !Number.isInteger(value.seat_count) || value.seat_count < 0 || !isNumber(value.pooled_allowance) || !Number.isInteger(value.pooled_allowance) || value.pooled_allowance < 0) throw new Error("Invalid billing subscription response");
  const currentPeriodStart = optionalDate(value.current_period_start, "Invalid billing subscription response");
  const currentPeriodEnd = optionalDate(value.current_period_end, "Invalid billing subscription response");
  const cancelledAt = optionalDate(value.cancelled_at, "Invalid billing subscription response");
  const graceUntil = optionalDate(value.grace_until, "Invalid billing subscription response");
  const paymentFailedAt = optionalDate(value.payment_failed_at, "Invalid billing subscription response");
  return { plan: requireString(value.plan, "Invalid billing subscription response"), status: requireString(value.status, "Invalid billing subscription response"), currency: requireString(value.currency, "Invalid billing subscription response"), amount_minor: value.amount_minor, billing_interval: requireString(value.billing_interval, "Invalid billing subscription response"), seat_count: value.seat_count, pooled_allowance: value.pooled_allowance, ...(currentPeriodStart ? { current_period_start: currentPeriodStart } : {}), ...(currentPeriodEnd ? { current_period_end: currentPeriodEnd } : {}), cancel_at_period_end: value.cancel_at_period_end, ...(cancelledAt ? { cancelled_at: cancelledAt } : {}), ...(graceUntil ? { grace_until: graceUntil } : {}), ...(paymentFailedAt ? { payment_failed_at: paymentFailedAt } : {}), payment_failure_count: value.payment_failure_count };
}
export function parseBillingStatus(value: unknown): BillingStatus {
  if (!isRecord(value) || value.contract_version !== "v1" || !billingModes.includes(value.billing_mode as typeof billingModes[number]) || !billingStates.includes(value.state_code as typeof billingStates[number]) || typeof value.commercial_entitled !== "boolean" || value.subscription !== null && value.subscription !== undefined && !isRecord(value.subscription)) throw new Error("Invalid billing status response");
  return { contract_version: "v1", workspace_id: requireString(value.workspace_id, "Invalid billing status response"), billing_mode: enumValue(value.billing_mode, billingModes, "Invalid billing status response"), state_code: enumValue(value.state_code, billingStates, "Invalid billing status response"), commercial_entitled: value.commercial_entitled, subscription: value.subscription === null ? null : parseBillingSubscription(value.subscription) };
}

export function parseUsageBudget(value: unknown): UsageBudget { if (!isRecord(value) || value.contract_version !== "v1" || value.status !== "active" && value.status !== "paused") throw new Error("Invalid usage budget response"); return { contract_version: "v1", workspace_id: requireString(value.workspace_id, "Invalid usage budget response"), capability: enumValue(value.capability, ["summary", "transcription", "workflow"] as const, "Invalid usage budget response"), period_start: requireString(value.period_start, "Invalid usage budget response"), period_end: requireString(value.period_end, "Invalid usage budget response"), limit_units: nonNegativeInteger(value.limit_units, "Invalid usage budget response"), used_units: nonNegativeInteger(value.used_units, "Invalid usage budget response"), reserved_units: nonNegativeInteger(value.reserved_units, "Invalid usage budget response"), remaining_units: nonNegativeInteger(value.remaining_units, "Invalid usage budget response"), status: value.status }; }
export function parseBillingAction(value: unknown): BillingAction { if (!isRecord(value) || value.contract_version !== "v1" || value.action_state !== "no_op" && value.action_state !== "redirect_ready" && value.action_state !== "unavailable" || value.action !== "checkout" && value.action !== "portal" || value.billing_mode !== "bypass" && value.billing_mode !== "shadow" && value.billing_mode !== "enforced" || typeof value.commercial_entitled !== "boolean") throw new Error("Invalid billing action response"); if (value.action_state === "redirect_ready" && typeof value.redirect_url !== "string") throw new Error("Invalid billing action redirect"); return { contract_version: "v1", workspace_id: requireString(value.workspace_id, "Invalid billing action response"), action: value.action, action_state: value.action_state, ...(value.reason_code === undefined ? {} : { reason_code: enumValue(value.reason_code, ["billing_mode_bypass", "billing_mode_shadow", "no_subscription", "action_unconfigured"] as const, "Invalid billing action response") }), billing_mode: value.billing_mode, state_code: enumValue(value.state_code, ["bypass", ...billingStates] as const, "Invalid billing action response"), commercial_entitled: value.commercial_entitled, ...(value.redirect_url === undefined ? {} : { redirect_url: requireString(value.redirect_url, "Invalid billing action response") }) }; }

export function parseSharedRecap(value: unknown): SharedRecap {
  if (!isRecord(value) || value.schema_version !== "shared-recap.v1" || !isRecord(value.report)) throw new Error("Invalid shared recap response");
  return { schema_version: "shared-recap.v1", meeting_id: requireString(value.meeting_id, "Invalid shared recap response"), title: requireString(value.title, "Invalid shared recap response"), platform: requireString(value.platform, "Invalid shared recap response"), report_id: requireString(value.report_id, "Invalid shared recap response"), version: typeof value.version === "number" && Number.isInteger(value.version) && value.version > 0 ? value.version : (() => { throw new Error("Invalid shared recap response"); })(), report: value.report };
}

export function parseOkResponse(value: unknown): OkResponse {
  if (!isRecord(value) || value.ok !== true) throw new Error("Invalid operation response");
  return { ok: true };
}

function parseConnector(value: unknown): Connector {
  if (!isRecord(value) || (value.provider !== "gmail" && value.provider !== "hubspot") || !Array.isArray(value.scopes) || !value.scopes.every(isString) || (value.status !== "active" && value.status !== "revoked" && value.status !== "expired" && value.status !== "reconciling") || (value.health_status !== "unknown" && value.health_status !== "healthy" && value.health_status !== "unhealthy" && value.health_status !== "reconciling")) throw new Error("Invalid connector response");
  return {
    id: requireString(value.id, "Invalid connector response"), workspace_id: requireString(value.workspace_id, "Invalid connector response"), owner_user_id: requireString(value.owner_user_id, "Invalid connector response"), provider: value.provider, external_account_id: requireString(value.external_account_id, "Invalid connector response"), account_type: requireString(value.account_type, "Invalid connector response"), api_version: requireString(value.api_version, "Invalid connector response"), scopes: value.scopes, status: value.status, health_status: value.health_status, created_at: requireString(value.created_at, "Invalid connector response"), updated_at: requireString(value.updated_at, "Invalid connector response"),
    ...(value.last_health_at === undefined || value.last_health_at === null ? {} : { last_health_at: requireString(value.last_health_at, "Invalid connector response") }), ...(value.revoked_at === undefined || value.revoked_at === null ? {} : { revoked_at: requireString(value.revoked_at, "Invalid connector response") }),
  };
}

export function parseConnectorList(value: unknown): ConnectorListResponse {
  if (!isRecord(value) || value.contract_version !== "connectors.v1" || !Array.isArray(value.connectors)) throw new Error("Invalid connector list response");
  return { contract_version: "connectors.v1", workspace_id: requireString(value.workspace_id, "Invalid connector list response"), connectors: value.connectors.map(parseConnector) };
}

export function parseConnectorInitiation(value: unknown): ConnectorInitiation {
  if (!isRecord(value) || value.contract_version !== "connectors.v1" || (value.provider !== "gmail" && value.provider !== "hubspot")) throw new Error("Invalid connector initiation response");
  return { contract_version: "connectors.v1", provider: value.provider, workspace_id: requireString(value.workspace_id, "Invalid connector initiation response"), authorization_url: requireString(value.authorization_url, "Invalid connector initiation response"), expires_at: requireString(value.expires_at, "Invalid connector initiation response") };
}

export function parseConnectorHealth(value: unknown): ConnectorHealthResponse {
  if (!isRecord(value) || value.contract_version !== "connectors.v1" || !isRecord(value.health) || (value.health.status !== "unknown" && value.health.status !== "healthy" && value.health.status !== "unhealthy" && value.health.status !== "reconciling")) throw new Error("Invalid connector health response");
  return { contract_version: "connectors.v1", health: { status: value.health.status, checked_at: requireString(value.health.checked_at, "Invalid connector health response"), ...(value.health.provider_code === undefined ? {} : { provider_code: requireString(value.health.provider_code, "Invalid connector health response") }) } };
}

function parsePrivacyPreferences(value: unknown) {
  if (!isRecord(value) || !isBoolean(value.ai_processing_enabled) || !isNumber(value.retention_days) || !Number.isInteger(value.retention_days) || value.retention_days < 1 || value.retention_days > 3650) {
    throw new Error("Invalid privacy preferences response");
  }
  return {
    ai_processing_enabled: value.ai_processing_enabled,
    retention_days: value.retention_days,
    updated_at: requireString(value.updated_at, "Invalid privacy preferences response"),
  };
}

export function parsePrivacyResponse(value: unknown): PrivacyResponse {
  if (!isRecord(value) || value.contract_version !== "v1") throw new Error("Invalid privacy response");
  return { contract_version: "v1", preferences: parsePrivacyPreferences(value.preferences) };
}

export function parseDeletionResult(value: unknown): DeletionResult {
  if (!isRecord(value) || value.status !== "completed" || !isNumber(value.cleanup_tasks_queued) || !Number.isInteger(value.cleanup_tasks_queued) || value.cleanup_tasks_queued < 0 || !isNumber(value.pending_cleanup_tasks) || !Number.isInteger(value.pending_cleanup_tasks) || value.pending_cleanup_tasks < 0 || !isStringArray(value.scope) || value.scope.length === 0 || (value.external_copy_status !== "not_tracked_by_memoru" && value.external_copy_status !== "reconciliation_required") || !isString(value.backup_expiry) || (value.device_purge_status !== "best_effort_device_purge" && value.device_purge_status !== "not_requested")) {
    throw new Error("Invalid deletion response");
  }
  return {
    status: "completed",
    requested_at: requireString(value.requested_at, "Invalid deletion response"),
    content_deleted_at: requireString(value.content_deleted_at, "Invalid deletion response"),
    cleanup_tasks_queued: value.cleanup_tasks_queued,
    pending_cleanup_tasks: value.pending_cleanup_tasks,
    scope: value.scope,
    external_copy_status: value.external_copy_status,
    backup_expiry: value.backup_expiry,
    device_purge_status: value.device_purge_status,
  };
}

export function parseSampleWorkflow(value: unknown): SampleWorkflow {
  if (!isRecord(value) || value.contract_version !== "v1" || value.synthetic !== true || !isRecord(value.meeting) || !isRecord(value.report) || !isRecord(value.follow_up) || !Array.isArray(value.evidence)) {
    throw new Error("Invalid sample workflow response");
  }
  if (value.report.schema_version !== "summary.v1" || !Array.isArray(value.report.decisions) || !Array.isArray(value.report.commitments)) {
    throw new Error("Invalid sample workflow report");
  }
  const evidence = value.evidence.map((item) => {
    if (!isRecord(item)) throw new Error("Invalid sample workflow evidence");
    return {
      id: requireString(item.id, "Invalid sample workflow evidence"),
      speaker: requireString(item.speaker, "Invalid sample workflow evidence"),
      text: requireString(item.text, "Invalid sample workflow evidence"),
      timestamp: requireString(item.timestamp, "Invalid sample workflow evidence"),
      source: requireString(item.source, "Invalid sample workflow evidence"),
    };
  });
  const parseItem = (item: unknown) => {
    if (!isRecord(item) || !Array.isArray(item.evidence) || !isStringArray(item.evidence)) throw new Error("Invalid sample workflow report item");
    return { id: requireString(item.id, "Invalid sample workflow report item"), text: requireString(item.text, "Invalid sample workflow report item"), evidence: item.evidence };
  };
  if (value.follow_up.sent !== false) throw new Error("Invalid sample workflow follow-up");
  return {
    contract_version: "v1",
    synthetic: true,
    workflow_id: requireString(value.workflow_id, "Invalid sample workflow response"),
    meeting: {
      id: requireString(value.meeting.id, "Invalid sample workflow meeting"),
      title: requireString(value.meeting.title, "Invalid sample workflow meeting"),
      platform: requireString(value.meeting.platform, "Invalid sample workflow meeting"),
      status: requireString(value.meeting.status, "Invalid sample workflow meeting"),
      started_at: requireString(value.meeting.started_at, "Invalid sample workflow meeting"),
    },
    evidence,
    report: {
      schema_version: "summary.v1",
      language: requireString(value.report.language, "Invalid sample workflow report"),
      summary: requireString(value.report.summary, "Invalid sample workflow report"),
      decisions: value.report.decisions.map(parseItem),
      commitments: value.report.commitments.map(parseItem),
    },
    follow_up: {
      status: requireString(value.follow_up.status, "Invalid sample workflow follow-up"),
      draft: requireString(value.follow_up.draft, "Invalid sample workflow follow-up"),
      sent: false,
    },
  };
}

export function parseExtensionHealth(value: unknown): ExtensionHealth {
  if (!isRecord(value) || value.contract_version !== "v1" || !isRecord(value.pairing) || !isRecord(value.capture)) throw new Error("Invalid extension health response");
  if (value.pairing.status !== "not_connected" && value.pairing.status !== "pending" && value.pairing.status !== "connected" && value.pairing.status !== "expired") throw new Error("Unsupported extension pairing status");
  if (value.pairing.recovery_state !== "none" && value.pairing.recovery_state !== "reconnect" && value.pairing.recovery_state !== "expired") throw new Error("Unsupported extension recovery state");
  if (!isString(value.capture.status) || (value.capture.recovery_state !== "none" && value.capture.recovery_state !== "resume_or_restart")) throw new Error("Invalid extension capture health");
  return {
    contract_version: "v1",
    pairing: { status: value.pairing.status, recovery_state: value.pairing.recovery_state, ...(value.pairing.expires_at === undefined ? {} : { expires_at: requireString(value.pairing.expires_at, "Invalid extension pairing expiry") }) },
    capture: { status: value.capture.status, recovery_state: value.capture.recovery_state, ...(value.capture.updated_at === undefined ? {} : { updated_at: requireString(value.capture.updated_at, "Invalid extension capture timestamp") }) },
  };
}

function parseWorkspace(value: unknown): Workspace {
  if (!isRecord(value)) throw new Error("Invalid workspace response");
  if (value.type !== "personal" && value.type !== "team") {
    throw new Error("Unsupported workspace type");
  }
  const role = value.role;
  if (
    role !== undefined &&
    role !== "owner" &&
    role !== "admin" &&
    role !== "member" &&
    role !== "billing_admin"
  ) {
    throw new Error("Unsupported workspace role");
  }
  const preferences = value.preferences;
  if (preferences !== undefined && (!isRecord(preferences) || Array.isArray(preferences))) {
    throw new Error("Invalid workspace preferences");
  }
  return {
    id: requireString(value.id, "Invalid workspace response"),
    type: value.type,
    name: requireString(value.name, "Invalid workspace response"),
    owner_user_id: requireString(value.owner_user_id, "Invalid workspace response"),
    preferences: preferences ? { ...preferences } : {},
    created_at: requireString(value.created_at, "Invalid workspace response"),
    updated_at: requireString(value.updated_at, "Invalid workspace response"),
    ...(role === undefined ? {} : { role }),
  };
}

export function parseWorkspaceList(value: unknown): Workspace[] {
  const items = Array.isArray(value)
    ? value
    : isRecord(value) && Array.isArray(value.workspaces)
      ? value.workspaces
      : isRecord(value) && Array.isArray(value.data)
        ? value.data
        : null;
  if (!items) throw new Error("Invalid workspaces response");
  return items.map(parseWorkspace);
}

export function parseWorkspaceMembership(value: unknown): WorkspaceMembership {
  if (!isRecord(value)) throw new Error("Invalid workspace membership response");
  if (
    value.status !== "active" &&
    value.status !== "revoked"
  ) {
    throw new Error("Unsupported workspace membership status");
  }
  if (
    value.role !== "owner" &&
    value.role !== "admin" &&
    value.role !== "member" &&
    value.role !== "billing_admin"
  ) {
    throw new Error("Unsupported workspace role");
  }
  return {
    workspace_id: requireString(value.workspace_id, "Invalid workspace membership response"),
    user_id: requireString(value.user_id, "Invalid workspace membership response"),
    role: value.role,
    status: value.status,
  };
}

export function parseWorkspaceInvitationPreview(value: unknown): WorkspaceInvitationPreview {
  if (!isRecord(value)) throw new Error("Invalid workspace invitation preview response");
  const workspace = value.workspace;
  if (!isRecord(workspace) || workspace.type !== "team") {
    throw new Error("Invalid workspace invitation preview response");
  }
  const defaultVisibility = workspace.default_visibility;
  if (defaultVisibility !== "private" && defaultVisibility !== "workspace") {
    throw new Error("Unsupported workspace visibility");
  }
  const inviter = value.inviter;
  if (!isRecord(inviter)) throw new Error("Invalid workspace invitation preview response");
  const role = value.role;
  if (role !== "admin" && role !== "member" && role !== "billing_admin") {
    throw new Error("Unsupported invitation role");
  }
  const expiresAt = requireString(value.expires_at, "Invalid workspace invitation preview response");
  if (Number.isNaN(Date.parse(expiresAt))) {
    throw new Error("Invalid workspace invitation expiry");
  }
  return {
    workspace: {
      id: requireString(workspace.id, "Invalid workspace invitation preview response"),
      type: "team",
      name: requireString(workspace.name, "Invalid workspace invitation preview response"),
      default_visibility: defaultVisibility,
    },
    inviter: {
      display_name: requireString(inviter.display_name, "Invalid workspace invitation preview response"),
    },
    role,
    expires_at: expiresAt,
  };
}

function parseResourcePath(value: unknown, message: string): string {
  const path = requireString(value, message);
  if (
    !path.startsWith("/") ||
    path.startsWith("//") ||
    path.includes("\\") ||
    path.includes("://") ||
    Array.from(path).some((character) => {
      const code = character.charCodeAt(0);
      return code <= 31 || code === 127;
    })
  ) {
    throw new Error(message);
  }
  return path;
}

function parseTodayItem(value: unknown, category: TodayCategory): TodayItem {
  if (!isRecord(value) || value.category !== category) throw new Error("Invalid Today item response");
  if (
    value.resource_type !== "meeting" &&
    value.resource_type !== "space" &&
    value.resource_type !== "commitment" &&
    value.resource_type !== "activity" &&
    value.resource_type !== "goal"
  ) throw new Error("Unsupported Today resource type");
  if (value.reason_code !== "deadline" && value.reason_code !== "dependency" && value.reason_code !== "recency") {
    throw new Error("Unsupported Today reason code");
  }
  return {
    id: requireString(value.id, "Invalid Today item response"),
    category,
    resource_type: value.resource_type,
    resource_id: requireString(value.resource_id, "Invalid Today item response"),
    title: requireString(value.title, "Invalid Today item response"),
    reason_code: value.reason_code,
    resource_path: parseResourcePath(value.resource_path, "Invalid Today resource path"),
    updated_at: requireString(value.updated_at, "Invalid Today item response"),
  };
}

export function parseTodayResponse(value: unknown): TodayResponse {
  if (!isRecord(value) || value.contract_version !== "v1" || !isString(value.workspace_id)) {
    throw new Error("Invalid Today response");
  }
  const sectionsValue = value.sections;
  if (!isRecord(sectionsValue)) throw new Error("Invalid Today response");
  const categories: TodayCategory[] = ["needs_review", "blocked", "due", "recent_verified", "follow_up"];
  const sections = Object.fromEntries(categories.map((category) => {
    const items = sectionsValue[category];
    if (!Array.isArray(items)) throw new Error("Invalid Today response");
    return [category, items.map((item) => parseTodayItem(item, category))];
  })) as Record<TodayCategory, TodayItem[]>;
  return { workspace_id: value.workspace_id, sections };
}

function parseSearchResult(value: unknown): SearchResult {
  if (!isRecord(value)) throw new Error("Invalid search result response");
  if (value.kind !== "space" && value.kind !== "meeting" && value.kind !== "commitment" && value.kind !== "segment") {
    throw new Error("Unsupported search result type");
  }
  const result: SearchResult = {
    kind: value.kind,
    id: requireString(value.id, "Invalid search result response"),
    title: requireString(value.title, "Invalid search result response"),
    resource_path: parseResourcePath(value.resource_path, "Invalid search resource path"),
  };
  if (value.snippet !== undefined) result.snippet = requireString(value.snippet, "Invalid search snippet");
  if (value.matched_segment_id !== undefined) result.matched_segment_id = requireString(value.matched_segment_id, "Invalid matched segment");
  if (value.updated_at !== undefined) result.updated_at = requireString(value.updated_at, "Invalid search result response");
  if (result.kind === "segment" && (!result.snippet || !result.matched_segment_id)) {
    throw new Error("Search segment is missing exact evidence");
  }
  return result;
}

export function parseSearchResponse(value: unknown): SearchResponse {
  if (!isRecord(value) || value.contract_version !== "v1") throw new Error("Invalid search response");
  if (!isString(value.workspace_id) || !isString(value.query) || !Array.isArray(value.items)) {
    throw new Error("Invalid search response");
  }
  if (value.next_cursor !== null && !isString(value.next_cursor)) throw new Error("Invalid search cursor");
  return {
    workspace_id: value.workspace_id,
    query: value.query,
    items: value.items.map(parseSearchResult),
    next_cursor: value.next_cursor,
  };
}

function parseLocale(value: unknown, fallback: "en" | "id" = "en"): "en" | "id" {
  if (value === undefined) return fallback;
  if (value === "en" || value === "id") return value;
  throw new Error("Unsupported locale in server response");
}

function parseTimezone(value: unknown, fallback = "UTC"): string {
  if (value === undefined) return fallback;
  if (!isString(value) || value.trim().length === 0) {
    throw new Error("Invalid timezone in server response");
  }
  try {
    new Intl.DateTimeFormat("en", { timeZone: value }).format(0);
  } catch {
    throw new Error("Invalid timezone in server response");
  }
  return value;
}

function parseSafetyLimits(value: unknown): SafetyLimits {
  if (!isRecord(value)) throw new Error("Invalid safety limits");
  const keys = [
    "max_transcript_chars",
    "max_segments",
    "max_audio_session_minutes",
    "max_agent_mutations",
    "max_agent_retry_attempts",
  ] as const;
  if (!keys.every((key) => isNumber(value[key]) && value[key] >= 0)) {
    throw new Error("Invalid safety limits");
  }
  return {
    max_transcript_chars: value.max_transcript_chars as number,
    max_segments: value.max_segments as number,
    max_audio_session_minutes: value.max_audio_session_minutes as number,
    max_agent_mutations: value.max_agent_mutations as number,
    max_agent_retry_attempts: value.max_agent_retry_attempts as number,
  };
}

export function parseAppConfig(value: unknown): AppConfig {
  if (!isRecord(value)) throw new Error("Invalid config response");
  if (value.contract_version !== "v1") {
    throw new Error("Unsupported config contract version");
  }
  if (
    !isBoolean(value.commercial_entitled) ||
    !isBoolean(value.would_commercially_allow) ||
    !isBoolean(value.monetization_enabled) ||
    !isString(value.web_app_url) ||
    !isString(value.app_version) ||
    !isString(value.billing_checkout_url) ||
    !isString(value.billing_portal_url)
  ) {
    throw new Error("Invalid config response");
  }
  const billingMode = value.billing_mode;
  if (billingMode !== "bypass" && billingMode !== "shadow" && billingMode !== "enforced") {
    throw new Error("Unsupported billing mode");
  }
  return {
    contract_version: "v1",
    billing_mode: billingMode,
    commercial_entitled: value.commercial_entitled,
    would_commercially_allow: value.would_commercially_allow,
    monetization_enabled: value.monetization_enabled,
    web_app_url: value.web_app_url,
    app_version: value.app_version,
    billing_checkout_url: value.billing_checkout_url,
    billing_portal_url: value.billing_portal_url,
    safety_limits: parseSafetyLimits(value.safety_limits),
  };
}

export function parseUserProfile(value: unknown): UserProfile {
  if (!isRecord(value)) throw new Error("Invalid profile response");
  if (value.plan !== "free" && value.plan !== "pro") {
    throw new Error("Unsupported user plan");
  }
  if (!isBoolean(value.monetization_enabled)) throw new Error("Invalid profile response");
  const limits = value.limits;
  if (
    limits !== null &&
    !(isRecord(limits) && isNumber(limits.summaries_per_month))
  ) {
    throw new Error("Invalid usage limits");
  }
  return {
    user_id: requireString(value.user_id, "Invalid profile response"),
    email: requireString(value.email, "Invalid profile response"),
    plan: value.plan,
    monetization_enabled: value.monetization_enabled,
    summaries_used_this_month: isNumber(value.summaries_used_this_month)
      ? value.summaries_used_this_month
      : 0,
    limits:
      limits === null
        ? null
        : { summaries_per_month: limits.summaries_per_month as number },
    locale: parseLocale(value.locale),
    timezone: parseTimezone(value.timezone),
    onboarding_progress: isNumber(value.onboarding_progress) ? value.onboarding_progress : 0,
    billing_mode:
      value.billing_mode === undefined
        ? undefined
        : value.billing_mode === "bypass" || value.billing_mode === "shadow" || value.billing_mode === "enforced"
          ? value.billing_mode
          : (() => { throw new Error("Unsupported billing mode"); })(),
    commercial_entitled:
      value.commercial_entitled === undefined
        ? undefined
        : isBoolean(value.commercial_entitled) ? value.commercial_entitled : (() => { throw new Error("Invalid commercial entitlement"); })(),
    would_commercially_allow:
      value.would_commercially_allow === undefined
        ? undefined
        : isBoolean(value.would_commercially_allow) ? value.would_commercially_allow : (() => { throw new Error("Invalid commercial capability"); })(),
    safety_limits: value.safety_limits === undefined ? undefined : parseSafetyLimits(value.safety_limits),
  };
}

export function parseUserPreferences(value: unknown): UserPreferences {
  if (!isRecord(value)) throw new Error("Invalid preferences response");
  return {
    locale: parseLocale(value.locale),
    timezone: parseTimezone(value.timezone),
  };
}

export function parseOnboardingProgress(value: unknown): OnboardingProgressResponse {
  if (!isRecord(value)) throw new Error("Invalid onboarding progress response");
  const progress = value.onboarding_progress;
  if (!isNumber(progress) || progress < 0 || progress > 3) {
    throw new Error("Invalid onboarding progress response");
  }
  return {
    locale: parseLocale(value.locale),
    timezone: parseTimezone(value.timezone),
    onboarding_progress: progress,
  };
}

export function parseTokenResponse(value: unknown): TokenResponse {
  if (!isRecord(value)) throw new Error("Invalid token response");
  if (!isString(value.access_token)) {
    throw new Error("Invalid token response");
  }
  if (value.refresh_token !== undefined && !isString(value.refresh_token)) {
    throw new Error("Invalid token response");
  }
  return {
    access_token: value.access_token,
    ...(value.refresh_token === undefined ? {} : { refresh_token: value.refresh_token }),
  };
}

export function parseMagicLinkResponse(value: unknown): MagicLinkResponse {
  if (!isRecord(value) || !isBoolean(value.ok)) throw new Error("Invalid magic link response");
  return {
    ok: value.ok === true,
    dev_magic_link: isString(value.dev_magic_link) ? value.dev_magic_link : undefined,
  };
}

export function parseMeetingList(value: unknown): MeetingListItem[] {
  if (!isRecord(value) || !Array.isArray(value.meetings)) {
    throw new Error("Invalid meetings list response");
  }
  return value.meetings.map((item) => {
    if (!isRecord(item)) throw new Error("Invalid meeting list item");
    if (!isOneOf(PLATFORMS, item.platform)) {
      throw new Error("Unsupported meeting platform");
    }
    if (!isOneOf(MEETING_STATUSES, item.status)) {
      throw new Error("Unsupported meeting status");
    }
    return {
      meeting_id: requireString(item.meeting_id, "Invalid meeting list item"),
      title: isString(item.title) ? item.title : "Untitled meeting",
      platform: item.platform,
      status: item.status,
      started_at: requireString(item.started_at, "Invalid meeting list item"),
      ended_at: item.ended_at === null || isString(item.ended_at) ? item.ended_at : null,
      segment_count: isNumber(item.segment_count) ? item.segment_count : 0,
      can_edit: item.can_edit === true,
      ...(Array.isArray(item.spaces) ? { spaces: item.spaces.map(parseSpace) } : {}),
      ...(isRecord(item.search_match) ? {
        search_match: {
          kind: enumValue(item.search_match.kind, ["title", "space", "transcript"] as const, "Invalid meeting search match"),
          snippet: requireString(item.search_match.snippet, "Invalid meeting search match"),
          count: integer(item.search_match.count, "Invalid meeting search match"),
          ...(isString(item.search_match.segment_id) ? { segment_id: item.search_match.segment_id } : {}),
          ...(isString(item.search_match.timestamp) ? { timestamp: item.search_match.timestamp } : {}),
        },
      } : {}),
    };
  });
}

export function parseMeetingListPage(value: unknown): MeetingListPage {
  if (!isRecord(value) || !Array.isArray(value.meetings)) {
    throw new Error("Invalid meetings page response");
  }

  // Older/local API deployments return the same meetings collection without
  // keyset metadata. It is still a valid first (and only) page.
  const includesPagination = "next_cursor" in value || "has_more" in value;
  if (
    includesPagination &&
    (typeof value.has_more !== "boolean" ||
      (value.next_cursor !== null && !isString(value.next_cursor)))
  ) {
    throw new Error("Invalid meetings page response");
  }
  const nextCursor = includesPagination && isString(value.next_cursor) ? value.next_cursor : null;
  const hasMore = includesPagination ? value.has_more === true : false;
  return {
    contract_version: "v1",
    meetings: parseMeetingList(value),
    next_cursor: nextCursor,
    has_more: hasMore,
    total: isNumber(value.total) && Number.isInteger(value.total) && value.total >= 0 ? value.total : null,
  };
}

const SEGMENT_FIELD_KEYS = {
  id: ["id", "ID"],
  speaker: ["speaker", "Speaker"],
  text: ["text", "Text"],
  timestamp: ["timestamp", "Timestamp"],
} as const;

function readSegmentField(
  seg: Record<string, unknown>,
  key: keyof typeof SEGMENT_FIELD_KEYS,
): string {
  for (const candidate of SEGMENT_FIELD_KEYS[key]) {
    const value = seg[candidate];
    if (isString(value)) return value;
  }
  return "";
}

export function parseMeetingCore(value: unknown): MeetingCore {
  if (!isRecord(value)) throw new Error("Invalid meeting detail response");
  if (!isOneOf(PLATFORMS, value.platform)) {
    throw new Error("Unsupported meeting platform");
  }
  if (!isOneOf(MEETING_STATUSES, value.status)) {
    throw new Error("Unsupported meeting status");
  }
  return {
    meeting_id: requireString(value.meeting_id, "Invalid meeting detail response"),
    title: isString(value.title) ? value.title : "Untitled meeting",
    platform: value.platform,
    status: value.status,
    started_at: requireString(value.started_at, "Invalid meeting detail response"),
    ended_at: value.ended_at === null || isString(value.ended_at) ? value.ended_at : null,
    can_edit: value.can_edit === true,
  };
}

function parseMeetingParticipants(value: unknown): MeetingParticipant[] {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => (isRecord(item) && isString(item.name) && item.name.trim() ? [{ name: item.name.trim() }] : []));
}

export function parseMeetingDetail(value: unknown): MeetingDetail {
  const core = parseMeetingCore(value);
  const record = value as Record<string, unknown>;
  const audio = parseMeetingAudioSummary(record.audio);
  return {
    ...core,
    participants: parseMeetingParticipants(record.participants),
    ...(Array.isArray(record.spaces) ? { spaces: record.spaces.map(parseSpace) } : {}),
    ...(audio ? { audio } : {}),
  };
}

const AUDIO_RECORDING_STATUSES = ["recording", "uploading", "processing", "ready", "failed", "deleted"] as const;
const AUDIO_TRANSCRIPTION_STATUSES = ["none", "queued", "processing", "ready", "failed"] as const;
const TRANSCRIPT_SOURCES = ["caption", "audio"] as const;
const TRANSCRIPT_CATEGORIES = ["transcript", "chat"] as const;

function nonNegative(value: unknown): number | null {
  return isNumber(value) && Number.isFinite(value) && value >= 0 ? value : null;
}

function parseUploadProgress(value: unknown): AudioUploadProgress | null {
  if (!isRecord(value)) return null;
  const uploaded = nonNegative(value.uploaded);
  const expected = nonNegative(value.expected);
  return uploaded !== null && expected !== null && expected > 0 ? { uploaded: Math.min(uploaded, expected), expected } : null;
}

/**
 * The optional `audio` block of meeting detail. Audio is an add-on to a meeting
 * that otherwise reads fine, so anything this client does not understand (a new
 * status, a malformed block) hides the block instead of failing the page.
 */
export function parseMeetingAudioSummary(value: unknown): MeetingAudioSummary | undefined {
  if (!isRecord(value)) return undefined;
  if (!isOneOf(AUDIO_RECORDING_STATUSES, value.recording_status) || !isOneOf(AUDIO_TRANSCRIPTION_STATUSES, value.transcription_status)) return undefined;
  return {
    recording_status: value.recording_status,
    transcription_status: value.transcription_status,
    transcription_enabled: value.transcription_enabled === true,
    upload_progress: parseUploadProgress(value.upload_progress),
    partial: value.partial === true,
    audio_end_offset_ms: nonNegative(value.audio_end_offset_ms),
    meeting_duration_ms: nonNegative(value.meeting_duration_ms) ?? 0,
  };
}

function parsePlayback(value: unknown): AudioPlayback | null {
  if (!isRecord(value) || !isString(value.url)) return null;
  // The link goes straight into <audio src>, so only plain web URLs are accepted.
  try {
    const protocol = new URL(value.url).protocol;
    if (protocol !== "https:" && protocol !== "http:") return null;
  } catch {
    return null;
  }
  return {
    url: value.url,
    expires_at: isString(value.expires_at) ? value.expires_at : "",
    mime_type: isString(value.mime_type) ? value.mime_type : "audio/webm",
    duration_ms: nonNegative(value.duration_ms) ?? 0,
    version: isNumber(value.version) ? value.version : 0,
  };
}

/** Pieces sorted by meeting time; a malformed or overlapping piece is dropped rather than mis-mapping time. */
function parsePieces(value: unknown): AudioPiece[] {
  if (!Array.isArray(value)) return [];
  const pieces = value.flatMap((item): AudioPiece[] => {
    if (!isRecord(item)) return [];
    const start = nonNegative(item.start_offset_ms);
    const end = nonNegative(item.end_offset_ms);
    const audioStart = nonNegative(item.audio_start_ms);
    return start !== null && end !== null && audioStart !== null && end > start ? [{ start_offset_ms: start, end_offset_ms: end, audio_start_ms: audioStart }] : [];
  }).sort((a, b) => a.start_offset_ms - b.start_offset_ms);
  const ordered: AudioPiece[] = [];
  for (const piece of pieces) {
    const last = ordered[ordered.length - 1];
    if (!last || piece.start_offset_ms >= last.end_offset_ms) ordered.push(piece);
  }
  return ordered;
}

function parsePeaks(value: unknown): AudioPeaks | null {
  if (!isRecord(value) || !isString(value.values_b64)) return null;
  const resolution = nonNegative(value.resolution_ms);
  return resolution ? { resolution_ms: resolution, values_b64: value.values_b64 } : null;
}

/** `GET /meetings/{id}/audio`; null when the statuses are ones this client does not know. */
export function parseMeetingAudio(value: unknown): MeetingAudio | null {
  if (!isRecord(value)) throw new Error("Invalid meeting audio response");
  if (!isOneOf(AUDIO_RECORDING_STATUSES, value.recording_status) || !isOneOf(AUDIO_TRANSCRIPTION_STATUSES, value.transcription_status)) return null;
  const ready = value.recording_status === "ready";
  return {
    recording_status: value.recording_status,
    transcription_status: value.transcription_status,
    transcription_enabled: value.transcription_enabled === true,
    partial: value.partial === true,
    meeting_duration_ms: nonNegative(value.meeting_duration_ms) ?? 0,
    playback: ready ? parsePlayback(value.playback) : null,
    pieces: parsePieces(value.pieces),
    peaks: parsePeaks(value.peaks),
  };
}

export function parseAudioTranscriptionRetry(value: unknown): AudioTranscriptionRetry {
  const status = isRecord(value) && isOneOf(AUDIO_TRANSCRIPTION_STATUSES, value.transcription_status) ? value.transcription_status : "queued";
  return { transcription_status: status };
}

/** The optional `offset_ms` and `source` of a transcript line or search hit; unknown values are left out. */
function segmentAudioFields(seg: Record<string, unknown>): { offset_ms?: number; source?: TranscriptSource } {
  const offset = nonNegative(seg.offset_ms);
  return {
    ...(offset !== null ? { offset_ms: offset } : {}),
    ...(isOneOf(TRANSCRIPT_SOURCES, seg.source) ? { source: seg.source } : {}),
  };
}

function countMap<T extends string>(keys: readonly T[], value: unknown): Record<T, number> {
  if (!isRecord(value)) throw new Error("Invalid meeting facets response");
  const out = {} as Record<T, number>;
  for (const key of keys) {
    const n = value[key];
    if (!isNumber(n) || !Number.isInteger(n) || n < 0) throw new Error("Invalid meeting facets response");
    out[key] = n;
  }
  return out;
}

export function parseMeetingFacets(value: unknown): MeetingFacets {
  if (!isRecord(value) || !isNumber(value.total) || !Number.isInteger(value.total) || value.total < 0) {
    throw new Error("Invalid meeting facets response");
  }
  return {
    total: value.total,
    platform: countMap(PLATFORMS, value.platform),
    status: countMap(MEETING_STATUSES, value.status),
    overview: countMap(OVERVIEW_STATUSES, value.overview),
  };
}

export function parseTranscriptPage(value: unknown): TranscriptPage {
  if (!isRecord(value)) throw new Error("Invalid transcripts response");
  const itemsRaw = Array.isArray(value.items) ? value.items : [];
  return {
    items: itemsRaw
      .map((seg) => {
        if (!isRecord(seg)) throw new Error("Invalid segment");
        const id = readSegmentField(seg, "id");
        const speaker = readSegmentField(seg, "speaker").trim() || "Unknown";
        const text = readSegmentField(seg, "text");
        const timestamp = readSegmentField(seg, "timestamp");
        return {
          id, speaker, text, timestamp, ...segmentAudioFields(seg),
          ...(isOneOf(TRANSCRIPT_CATEGORIES, seg.category) ? { category: seg.category } : {}),
          ...(isString(seg.updated_at) && Number.isFinite(Date.parse(seg.updated_at)) ? { updated_at: seg.updated_at } : {}),
        };
      })
      .filter((segment) => segment.text.trim().length > 0),
    next_cursor: value.next_cursor === null || isString(value.next_cursor) ? value.next_cursor : null,
    has_more: value.has_more === true,
  };
}

const TRANSCRIPT_MATCH_TYPES = ["exact", "similar", "sounds"] as const;

function parseSearchRanges(value: unknown, textLength: number): Array<[number, number]> {
  if (!Array.isArray(value)) throw new Error("Invalid transcript search hit");
  let previousEnd = 0;
  return value.map((range) => {
    if (!Array.isArray(range) || range.length !== 2) throw new Error("Invalid transcript search range");
    const [start, end] = range;
    // A malformed range would slice the wrong text, so reject it outright.
    if (!isNumber(start) || !isNumber(end) || !Number.isInteger(start) || !Number.isInteger(end) || start < previousEnd || end <= start || end > textLength) {
      throw new Error("Invalid transcript search range");
    }
    previousEnd = end;
    return [start, end] as [number, number];
  });
}

export function parseTranscriptSearchResult(value: unknown): TranscriptSearchResult {
  if (!isRecord(value) || !Array.isArray(value.items)) throw new Error("Invalid transcript search response");
  return {
    query: isString(value.query) ? value.query : "",
    items: value.items.map((hit) => {
      if (!isRecord(hit) || !isString(hit.segment_id) || !isString(hit.text)) throw new Error("Invalid transcript search hit");
      return {
        segment_id: hit.segment_id,
        // Same normalization as transcript pages, so speaker filters and colors agree.
        speaker: (isString(hit.speaker) ? hit.speaker : "").trim() || "Unknown",
        timestamp: isString(hit.timestamp) ? hit.timestamp : "",
        text: hit.text,
        match_type: enumValue(hit.match_type, TRANSCRIPT_MATCH_TYPES, "Invalid transcript search match type"),
        matched_terms: isNumber(hit.matched_terms) ? hit.matched_terms : 1,
        ranges: parseSearchRanges(hit.ranges, hit.text.length),
        cursor: isString(hit.cursor) ? hit.cursor : null,
        ...segmentAudioFields(hit),
      };
    }),
    total_matches: isNumber(value.total_matches) ? value.total_matches : value.items.length,
    truncated: value.truncated === true,
  };
}

export function parseMeetingSummary(value: unknown): MeetingSummary | null {
  if (value === null) return null;
  if (!isRecord(value) || !isString(value.text)) throw new Error("Invalid meeting summary response");
  const status = value.status === undefined ? "ready" : enumValue(value.status, ["queued", "running", "ready", "failed", "skipped"] as const, "Invalid meeting summary status");
  return {
    status,
    ...(isString(value.skip_reason) ? { skip_reason: value.skip_reason } : {}),
    summary_id: isString(value.summary_id) ? value.summary_id : "",
    text: value.text,
    action_items: isStringArray(value.action_items) ? value.action_items : [],
    decisions: isStringArray(value.decisions) ? value.decisions : [],
    generated_at: isString(value.generated_at) ? value.generated_at : "",
  };
}

export function parseSummarizeResponse(value: unknown): SummarizeResponse {
  if (!isRecord(value)) throw new Error("Invalid summarize response");
  const usage = value.usage;
  return {
    summary_id: isString(value.summary_id) ? value.summary_id : "",
    summary: isString(value.summary) ? value.summary : "",
    action_items: isStringArray(value.action_items) ? value.action_items : [],
    decisions: isStringArray(value.decisions) ? value.decisions : [],
    usage:
      isRecord(usage) && isNumber(usage.summaries_used_this_month)
        ? {
            summaries_used_this_month: usage.summaries_used_this_month,
            limit: usage.limit === null || isNumber(usage.limit) ? usage.limit : null,
          }
        : undefined,
  };
}

export function parseAsyncSummaryState(value: unknown): AsyncSummaryState {
  if (!isRecord(value) || !isString(value.meeting_id) || !isString(value.job_id)) {
    throw new Error("Invalid summary status response");
  }
  if (value.status !== "queued" && value.status !== "running" && value.status !== "done" && value.status !== "failed") {
    throw new Error("Invalid summary status response");
  }
  const outputLanguage = value.output_language;
  if (outputLanguage !== undefined && outputLanguage !== "en" && outputLanguage !== "id" && outputLanguage !== "mixed") {
    throw new Error("Invalid summary status response");
  }
  const template = value.template;
  if (template !== undefined && template !== "general" && template !== "customer_call" && template !== "customer_success") {
    throw new Error("Invalid summary status response");
  }
  const result = isRecord(value.result) ? value.result : undefined;
  return {
    meeting_id: value.meeting_id, job_id: value.job_id, status: value.status,
    output_language: outputLanguage,
    template: template as SummaryTemplate | undefined,
    attempt: isNumber(value.attempt) ? value.attempt : undefined,
    poll_after_seconds: isNumber(value.poll_after_seconds) ? value.poll_after_seconds : undefined,
    error_code: isString(value.error_code) ? value.error_code : undefined,
    error_message: isString(value.error_message) ? value.error_message : undefined,
    can_retry: isBoolean(value.can_retry) ? value.can_retry : undefined,
    result: result && isString(result.summary_id) && isString(result.url) && isString(result.url_expires_at) && isString(result.sha256) && isNumber(result.size_bytes) && isString(result.generated_at)
      ? { summary_id: result.summary_id, format: "text/markdown", url: result.url, url_expires_at: result.url_expires_at, sha256: result.sha256, size_bytes: result.size_bytes, generated_at: result.generated_at }
      : undefined,
  };
}

export function parseApiErrorBody(value: unknown): ApiErrorBody {
  if (!isRecord(value)) return {};
  const category = value.category;
  return {
    error: isString(value.error) ? value.error : undefined,
    code: isString(value.code) ? value.code : undefined,
    category:
      category === "commercial" || category === "authorization" || category === "safety" ||
      category === "quota" || category === "provider"
        ? category
        : undefined,
    limit: isNumber(value.limit) ? value.limit : undefined,
    used: isNumber(value.used) ? value.used : undefined,
    resets_at: isString(value.resets_at) ? value.resets_at : undefined,
    details: isRecord(value.details) ? value.details : undefined,
  };
}

export function parseConnectExtensionResponse(value: unknown): ConnectExtensionResponse {
  if (!isRecord(value)) throw new Error("Invalid connect extension response");
  if (!isString(value.connect_code) || !isNumber(value.expires_in)) {
    throw new Error("Invalid connect extension response");
  }
  return { connect_code: value.connect_code, expires_in: value.expires_in };
}

export function parseSessionList(value: unknown): AuthSession[] {
  const sessions = Array.isArray(value)
    ? value
    : isRecord(value) && Array.isArray(value.sessions)
      ? value.sessions
      : null;
  if (!sessions) {
    throw new Error("Invalid sessions response");
  }
  return sessions.map((item) => {
    if (!isRecord(item) || !isString(item.id)) throw new Error("Invalid session");
    const client: SessionClient =
      item.client === "extension" ? "extension" : item.client === "legacy" ? "legacy" : "web";
    return {
      id: item.id,
      client,
      ...(isString(item.user_agent) ? { user_agent: item.user_agent } : {}),
      ...(isString(item.ip_address) ? { ip_address: item.ip_address } : {}),
      created_at: isString(item.created_at) ? item.created_at : "",
      ...(isString(item.last_seen_at) ? { last_seen_at: item.last_seen_at } : {}),
      expires_at: isString(item.expires_at) ? item.expires_at : "",
      current: item.current === true,
    };
  });
}
